import { CalculatedValues, DataExtractor, WPAndMetadata, WPSections } from './data-extractor';

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LegSpeedSettings, VesselCharacteristics, VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import '../../../assets/fonts/jsPDF/MaerskText';
import { isOnline } from '../../../src/utils/helpers';

const MpsToKts = 60*60 / 1852;

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

export class VoyagePassagePlanPdf
{
    constructor(plan: VoyagePlan)
    {
        this._doc = new jsPDF({orientation: "landscape", lineHeight: 1, unit: 'mm', format: 'a4'});
        this._doc.setFont("MaerskText", "normal");
        this._plan = plan;
    }

    async generatePdf(isOfflinePlan: boolean = false, showPassagePlanFromPlan?: boolean)
    {   
        let vesselCharacteristics: VesselCharacteristics|null = null;
        let calculatedValues: CalculatedValues|null = null;

        const currentSelectedRoute = this._plan.routes.find(route => route.routeId === this._plan.currentSelectedRouteId);

        const shouldGenerateCalculatedValues = isVesselOnCloud && !isOnline() && currentSelectedRoute?.recalcNeeded;
        const isVesselOnCloudOffline = isVesselOnCloud && !isOnline();
        
        vesselCharacteristics = await VesselPlanningModel!.getVesselCharacteristics();
        calculatedValues = shouldGenerateCalculatedValues ? DataExtractor.addOfflineCalculatedValues(this._plan, vesselCharacteristics) : DataExtractor.getCalculatedValues(this._plan, vesselCharacteristics!, isOfflinePlan, showPassagePlanFromPlan);
        
        this._header(this._plan.departureTerminalCode, this._plan.arrivalTerminalCode);
        this._information(this._plan.departureTerminalCode, this._plan.arrivalTerminalCode, calculatedValues, isOfflinePlan, isVesselOnCloudOffline);
        this._reminders();
        this._chartsAndLights();
        this._publications();
        this._approvals();
        
        const waypoints = DataExtractor.populateWaypoints(this._plan, vesselCharacteristics?.ecdisWPStartIndex ?? null, isOfflinePlan, showPassagePlanFromPlan);

        if (waypoints.berthToPilot.length !== 0) {
            this._generateWaypoints(waypoints.berthToPilot, vesselCharacteristics, 'Berth to pilot', this._plan.departureTerminalCode, 'OUT',
                                    <string>DataExtractor.getDataFromPlan('departurePort', this._plan), 'out', WPSections.BerthToPilot);
        }
        if (waypoints.pilotToPilot.length !== 0) {
            this._generateWaypoints(waypoints.pilotToPilot, vesselCharacteristics, 'Pilot to pilot', this._plan.departureTerminalCode, this._plan.arrivalTerminalCode,
                                    <string>DataExtractor.getDataFromPlan('departurePort', this._plan),
                                    <string>DataExtractor.getDataFromPlan('arrivalPort', this._plan), WPSections.PilotToPilot);
        }
        if (waypoints.pilotToBerth.length !== 0) {
            this._generateWaypoints(waypoints.pilotToBerth, vesselCharacteristics, 'Pilot to berth', this._plan.arrivalTerminalCode, 'IN',
                                    <string>DataExtractor.getDataFromPlan('arrivalPort', this._plan), 'in', WPSections.PilotToBerth);
        }

        if (waypoints.berthToBerth.length !== 0) {
            this._generateWaypoints(waypoints.berthToBerth, vesselCharacteristics, 'Berth to berth', this._plan.arrivalTerminalCode, this._plan.arrivalTerminalCode,
                                    <string>DataExtractor.getDataFromPlan('arrivalPort', this._plan),
                                    <string>DataExtractor.getDataFromPlan('arrivalPort', this._plan), WPSections.BerthToBerth);
        }

        if (calculatedValues) {
            this._speedAndDistancesAndDraughtsOnPassage(calculatedValues, vesselCharacteristics!);
            this._squatsAndUderKeelClearance(calculatedValues);
        }
        this._safetyMessages();
        this._weatherInformation();
        this._statusOfNavigation();
        this._mastersOrders();

        this._teamList('At departure the Bridge Team will consist of:', this._getDepartureBridgeTeam());
        this._teamList('At arrival the Bridge Team will consist of:', this._getArrivalBridgeTeam());
        this._various();
     
        this._chartGuidelines();
    }

    save()
    {
        this._doc.save(`Passage plan ${this._plan.departureTerminalCode}-${this._plan.arrivalTerminalCode} ${new Date(Date.now()).toISOString()}.pdf`);
    }

    showFrame()
    {
        return this._doc.output('datauristring');
    }

    private _plan: VoyagePlan;
    private _doc: any;

    private _waypointsData(waypoints: WPAndMetadata[], vesselCharacteristics: VesselCharacteristics | null, wpSection: WPSections) {
        const formatCourse = (course: number|null|undefined) => {
            if (course == null) {
                return "";
            }
            let courseParts = course.toFixed(1).split(".");
            return `${courseParts[0]}°${courseParts[1]}`;
        };

        const formatPosition = (number: number, type: "lat"|"lon") => {
            const degrees = Math.floor(Math.abs(number));
            const minutes = (Math.abs(number) - degrees)*60;
            let hemisphere;
            if (type === "lat")
            {
                hemisphere = number >= 0 ? "N" : "S"
            }
            else
            {
                hemisphere = number >= 0 ? "E" : "W";
            }
            return `${degrees}°${minutes.toFixed(2)}${hemisphere}`;
        };

        const formatSquat = (squat: number|undefined, openWaters: boolean|undefined, idx: number, speed_maximum?: number) => {
            if (squat === undefined) {
                return "";
            }
            if (openWaters === undefined) {
                // Default is confined waters for berth -> pilot and pilot -> berth, open waters for pilot -> pilot and berth -> berth
                if ((wpSection === WPSections.BerthToPilot && idx === waypoints.length - 1) ||
                    ((wpSection === WPSections.PilotToPilot || wpSection === WPSections.BerthToBerth) && idx !== waypoints.length - 1))
                {
                    openWaters = true;
                }
                else
                {
                    openWaters = false;
                }
            }

            let legSpeedSetting = ""
            if (vesselCharacteristics) {
                if( vesselCharacteristics.legSpeedSetting === LegSpeedSettings.MAX_SPEED && speed_maximum ){
                    legSpeedSetting = "M "
                } else {
                    legSpeedSetting = "P "
                }
            }

            return `${openWaters ? "O" : "C"}${legSpeedSetting}${squat.toFixed(2)} m`;
        }

        return waypoints.map( (waypoint, index) => {
            const result = [
                waypoint.metadata.sequenceNumber,
                formatPosition(waypoint.coordinates[1], "lat"),
                formatPosition(waypoint.coordinates[0], "lon"),
                waypoint.metadata.legType,
                formatCourse(waypoint.metadata.course),
                DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "rate_of_turn") ? DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "rate_of_turn") + " °/min" : "",
                DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "turn_radius") ? DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "turn_radius") + " nm" : "",
                `${(waypoint.metadata.distance.toNextWaypoint!/1.852).toFixed(1)} nm`,
                `${(waypoint.metadata.distance.remaining/1.852).toFixed(1)} nm`,
                waypoint.waypoint.properties.speed_minimum != undefined ? `${(waypoint.waypoint.properties.speed_minimum*MpsToKts).toFixed(1)} knots` : "",
                waypoint.waypoint.properties.speed_maximum != undefined ? `${(waypoint.waypoint.properties.speed_maximum*MpsToKts).toFixed(1)} knots` : "",
                waypoint.waypoint.properties.name || ""
            ];

            if (wpSection === WPSections.PilotToPilot || wpSection === WPSections.BerthToBerth)
            {
                const catzoc = DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "catzoc") !== undefined ? DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "catzoc") : "";
                result.push(catzoc);
            }

            const minDepthValue = DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "min_depth");
            const minDepth = (minDepthValue !== undefined && minDepthValue !== null) ? `${Number(minDepthValue).toFixed(1)} m` : "0 m";
            const tideValue = DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "tide");
            const tide = (tideValue !== undefined && tideValue !== null) ? `${Number(tideValue).toFixed(2)} m` : "";
            result.push(waypoint.waypoint.properties.lop ? waypoint.waypoint.properties.lop : "",
                        DataExtractor.getUserOrSystemInputFromWP(waypoint.waypoint, "max_x_track") || 0,
                        minDepth,
                        tide,
                        formatSquat(waypoint.waypoint.properties.squat, waypoint.waypoint.properties.openWaters, index, waypoint.waypoint.properties.speed_maximum),
                        waypoint.waypoint.properties.ukc != undefined ? waypoint.waypoint.properties.ukc.toFixed(1) + " m" : "");

            return result;
        });
    }

    private _generateWaypoints(waypoints: WPAndMetadata[], vesselCharacteristics: VesselCharacteristics | null, header: string, from: string, to: string,
                               longFrom: string, longTo: string, wpSections: WPSections)
    {
        const totalDistance = (waypoints.reduce( (acc, wpInfo, i) => i !== waypoints.length-1 ? acc + wpInfo.metadata.distance.toNextWaypoint!: acc, 0))/1.852;
        const waypointsData = this._waypointsData(waypoints, vesselCharacteristics, wpSections);

        autoTable(this._doc, {
            theme: 'plain',
            columnStyles: {0: { fontStyle: 'bold', fontSize: 8, halign: 'left' }, 1: { fontStyle: 'bold', fontSize: 8, halign: 'right' }},
            rowPageBreak: 'auto',
            body: [[`${from} - ${to}`, `${longFrom} ${longTo}`]],
            styles: { font: "MaerskText" },
        });

        const passagePlanSpan = wpSections === WPSections.PilotToPilot || wpSections === WPSections.BerthToBerth ? 7 : 6;
        const totalColumns = 12 + passagePlanSpan;
        const head = [[{ content: 'WP\nNumbers', colSpan: 1}, { content: 'Waypoint\nposition', colSpan: 2}, 'RL', { content: 'Course', rowSpan: 2}, { content: 'Turnrate (°/min)', rowSpan: 2}, { content: 'Turn Circle (nm)', rowSpan: 2}, { content: 'Distance', colSpan: 2}, { content: 'Speed', colSpan: 2}, { content: 'Waypoint\nDetails', rowSpan: 2}, { content: 'Passage\nplan', colSpan: passagePlanSpan}],
        ['Seq\n#', 'Latitude', 'Longitude', 'GC', 'To\nWaypoint', 'Remaining', 'Min', 'Max']];
        if (wpSections === WPSections.PilotToPilot || wpSections === WPSections.BerthToBerth)
        {
            head[1].push('CatZoc');
        }
        head[1].push('Position cross\ncheck interval', 'Max.\nX track (nm)', 'Min.\ndepth', 'Tide (m)', 'Squat*', 'UKC')

        autoTable(this._doc, {
            startY: (this._doc as any).lastAutoTable.finalY + 5,
            theme: 'plain',
            columnStyles: {0: { fontStyle: 'bold', fontSize: 8}, 1: { cellWidth: 'wrap'}, 2: { cellWidth: 'wrap'}, 3: {cellWidth: 'wrap'}, 4: { cellWidth: 'wrap'}},
            headStyles: { fontStyle: 'bold', fontSize: 8, halign: 'center' },
            rowPageBreak: 'auto',
            styles: {
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                cellPadding: {
                    top: 1,
                    right: 1,
                    left: 1,
                    bottom: 1
                },
                font: "MaerskText",
                fontSize: 8,
            },
            head: head,
            body: [ ...waypointsData,
                   [{ content: '* C - Confined Waters   O - Open Waters   M - Squat based on max leg speed   P - Squat based on calculated planned leg speed', colSpan: totalColumns, styles: { fontSize: 8, fontStyle: 'normal' }}],
                   [{ content: `${header} distance: ${totalDistance.toFixed(1)} nm`, colSpan: totalColumns}],
                   [{ content: 'Have in mind, that positions of buoys might differ between the chart and the actual positions.\nBuoys may also be extinguised or missing, ask the pilot if there is any conditions we should know about.\nDo not rely on the one position reference only, especially not on buoys.', colSpan: totalColumns}]
                  ],

        });
    }

    private _header(from: string, to: string) {
        const description = `${from} - ${to}`;
        this._doc.setFontSize(11);
        this._doc.setFont("MaerskText", "bold");
        this._doc.text(`ROUTE - ${description}`, 16, 20);
    }

    private _information(from: string, to: string, calculatedValues: CalculatedValues|null, isOfflinePlan?: boolean, isVesselOnCloudOffline?: boolean) {
        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: 40,
            head: [[{ content: 'Information', colSpan: 2}]],
            body: [['Description',  DataExtractor.getDataFromPlan("description", this._plan)],
                   ['Departure port', DataExtractor.getDataFromPlan('departurePort', this._plan)],
                   ['Arrival port', DataExtractor.getDataFromPlan('arrivalPort', this._plan)],
                   ['Port memo', DataExtractor.getDataFromPlan('portMemo', this._plan)],
                   ['Buffer speed', `${!isOfflinePlan ? !isVesselOnCloudOffline ? calculatedValues?.routeAverageSpeed!.toFixed(1) + " kt" : "": ''}`]],
            theme: 'plain',
            headStyles: { fontStyle: 'bold', fontSize: 11 },
            columnStyles: {0: { fontStyle: 'bold', fontSize: 8, cellWidth: 35 }},
            rowPageBreak: 'auto',
            styles: { cellWidth: 'wrap', font: "MaerskText" },
        });
    }

    private _reminders() {
        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: (this._doc as any).lastAutoTable.finalY + 5,
            head: [],
            body: [['Reminders', `${DataExtractor.getDataFromPlan("reminders", this._plan)?.toString()?.replaceAll('\t', '')}`]],
            theme: 'plain',
            columnStyles: {0: { fontStyle: 'bold', fontSize: 8, cellWidth: 35 }, 1: { halign: 'left', cellWidth: 'auto'}},
            rowPageBreak: 'auto',
            styles: { font: "MaerskText" },
        });
    }

    private _chartsAndLights() {
        const currentSelectedRouteIndex = this._plan!.routes.findIndex(route => route.routeId === this._plan!.currentSelectedRouteId) ?? this._plan?.selectedRouteIndex;
        const notEcdisCompliant: boolean = this._plan.routes[currentSelectedRouteIndex]?.passagePlan!.notEcdisCompliant
        const type = notEcdisCompliant ? "Non-operational ECDIS or voyage with ports not covered by ENC" :
                                          "Fully operational ECDIS with a full voyage of ENC charts";

        const nestedTableCell = {
            content: '',
            // Dynamic height of nested tables are not supported right now
            // so we need to define height of the parent cell
            styles: { minCellHeight: 40 },
        }

        const doc = this._doc;

        const body: ((string | number)[] | (string | {
            content: string;
            styles: {
                minCellHeight: number;
            };
            })[])[] = [
            ['Type of passage plan', type ],
            ['Paper charts:', DataExtractor.getDataFromPlan("paperCharts", this._plan)],
            ['ENC Cells', `${DataExtractor.getDataFromPlan("encCellsUsed", this._plan)}`],
            ['Book & tables', `${DataExtractor.getDataFromPlan("booksAndTables", this._plan)}`],
            ['Charts & lights notes', DataExtractor.getDataFromPlan("chartsAndLightsNotes", this._plan)]
        ]

        if (!notEcdisCompliant) {
            body.push(['Position Plotting Method', nestedTableCell])
        } else {
            body.push(['Position Plotting Method', "As per Procedure P840; Flowchart - ECDIS Failure"])
        }

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: (this._doc as any).lastAutoTable.finalY + 5,
            head: [[{ content: 'Chart & lights', colSpan: 2}]],
            body: body,
            didDrawCell: function (data: { row: { index: number; section: string; }; column: { index: number; }; cell: { y: number; width: number; x: number; }; }) {
                if (data.row.index === 5 && data.row.section === 'body' && data.column.index === 1 && !notEcdisCompliant) {
                    autoTable(doc, {
                      startY: data.cell.y + 2,
                      tableWidth: data.cell.width - 4,
                      margin: { left: data.cell.x + 2 },
                      head: [['Primary position plotting method', 'Position cross check method', 'Position cross check intervals']],
                      body: [['GPS derived position plotted\nautomatically by ECDIS.','i. Secondary position fixing source on ECDIS\n\nii. Radar Overlay on ECDIS\n\niii. Parallel Indexing on Radar\n\niv. Lines of Position on ECDIS','Passage plan shall specify the intervals between\nposition cross checks, frequency of which shall be\nbased on the proximity of navigation hazards.']],
                      theme: 'plain',
                      headStyles: { fontStyle: 'bold', fontSize: 8 },
                      rowPageBreak: 'auto',

                      styles: { cellWidth: 'wrap',
                                halign: 'left',
                                lineColor: [0, 0, 0],
                                lineWidth: 0.2,
                                font: "MaerskText",
                                fontSize: 8
                      }
                  });
            }},
            theme: 'plain',
            columnStyles: {0: { fontStyle: 'bold', fontSize: 8, cellWidth: 30  }, 1: { halign: 'left', cellWidth: 'auto' }},
            headStyles: { fontStyle: 'bold', fontSize: 11 },
            rowPageBreak: 'avoid',
            styles: { cellWidth: 'wrap', font: "MaerskText" },
        });
    }

    private _publications() {
        this._doc.setFont("MaerskText", "normal");
        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: (this._doc as any).lastAutoTable.finalY + 5,
            margin: 0,
            head: [[{ content: 'Publications', colSpan: 2}]],
            body: [['Sailing directions (pilots) to be used on passage:', Array.from(this._doc.splitTextToSize(`${DataExtractor.getDataFromPlan("sailingDirections", this._plan)}`, 160)).join('\n').replace('\n\n', '\n')],
                   ['List of radio signals for voyage', `${DataExtractor.getDataFromPlan("listOfRadioSignals", this._plan)}`],
                   ['List of lights for voyage:', `${DataExtractor.getDataFromPlan("listOfLights", this._plan)}`],
                   ['Port Memo:',`${DataExtractor.getDataFromPlan('portMemo', this._plan)}`],
                   ['GPS/ECDIS/Radar map:', `${DataExtractor.getDataFromPlan('gpsEcdisRadarMap', this._plan)}`],
                   ['Publications corrected to NTM week no.:',`${DataExtractor.getDataFromPlan("pubCorrectedToWeek", this._plan)}`],
                   ['Tide tables / program:',`${DataExtractor.getDataFromPlan("tideTables", this._plan)}`],
                   ['Publications notes:',`${DataExtractor.getDataFromPlan("publicationsNotes", this._plan)}`],
                  ],
            theme: 'plain',
            columnStyles: {0: { fontStyle: 'bold', fontSize: 8, cellWidth: 68 }, 1: { halign: 'left', cellWidth: 'auto' }},
            headStyles: { fontStyle: 'bold', fontSize: 11 },
            rowPageBreak: 'auto',
            styles: { font: "MaerskText" },
        });
    }

    private _formatTime(time: number) {
        return `${Math.floor(time)} h ${Math.floor((time - Math.floor(time))*60)} min`;
    }

    private _speedAndDistancesAndDraughtsOnPassage(calculatedValues: CalculatedValues, vesselCharacteristics: VesselCharacteristics) {
        // start on new page
        let startY = (this._doc as any).lastAutoTable.finalY + 7;

        if ((this._doc as any).lastAutoTable.finalY > 81) {
            this._doc.addPage();
            startY = 10;
        }

        const berthToPilotDistance = `${calculatedValues.berthToPilot !== null ? calculatedValues.berthToPilot.toFixed(1) : 0} nm`;
        const pilotToPilotDistance = `${calculatedValues.pilotToPilot !== null ? calculatedValues.pilotToPilot.toFixed(1) : 0} nm`;
        const pilotToBerthDistance = `${calculatedValues.pilotToBerth !== null ? calculatedValues.pilotToBerth.toFixed(1): 0} nm`;
        const totalDistance = `${calculatedValues.totalDistance.toFixed(1)} nm`;
        const timeBerthToPilot = `${this._formatTime(calculatedValues.timeBerthToPilot)}`;
        const timePilotToPilot = `${this._formatTime(calculatedValues.timePilotToPilot)}`;
        const timePilotToBerth = `${this._formatTime(calculatedValues.timePilotToBerth)}`;
        const timeTotal = `${this._formatTime(calculatedValues.timeTotal)}`;
        const speedBerthToPilot = `${calculatedValues.speedBerthToPilot.toFixed(1)} kt`;
        const speedPilotToPilot = `${calculatedValues.speedPilotToPilot.toFixed(1)} kt`;
        const speedPilotToBerth = `${calculatedValues.speedPilotToBerth.toFixed(1)} kt`;
        const speedTotal = `${calculatedValues.speedTotal.toFixed(1)} kt`;

        const nestedTableCell = {
            content: '',
            // Dynamic height of nested tables are not supported right now
            // so we need to define height of the parent cell
            styles: { minCellHeight: 40, cellWidth: 125 },
        }

        const doc = this._doc;
        const plan = this._plan;

        autoTable(doc, {
            theme: "plain",
            head: [['Speed and distances', 'Planned draughts on passage']],
            body: [[nestedTableCell]],
            startY: startY,
            didDrawCell: function (data: { row: { index: number; section: string; }; column: { index: number; }; cell: { y: number; width: number; x: number; }; }) {
              if (data.row.index === 0 && data.row.section === 'body' && data.column.index === 0) {
                autoTable(doc, {
                    startY: data.cell.y + 2,
                    tableWidth: data.cell.width - 4,
                    margin: { left: data.cell.x + 2 },
                    head: [[{ content: 'Distances', colSpan: 2}, 'Speed', 'Time']],
                    body: [['Berth to pilot:', berthToPilotDistance, speedBerthToPilot, timeBerthToPilot],
                           ['Pilot to pilot:', pilotToPilotDistance, speedPilotToPilot, timePilotToPilot],
                           ['Pilot to berth:', pilotToBerthDistance, speedPilotToBerth, timePilotToBerth],
                           ['Total Distance:', totalDistance, speedTotal, timeTotal]],
                    theme: 'plain',
                    headStyles: { fontStyle: 'bold', fontSize: 8 },
                    rowPageBreak: 'auto',

                    styles: { cellWidth: 'wrap',
                              halign: 'left',
                              lineColor: [0, 0, 0],
                              lineWidth: 0.2,
                              font: "MaerskText",
                              fontSize: 8
                    }
                });
              }
              if (data.row.index === 0 && data.row.section === 'body' && data.column.index === 1) {
                autoTable(doc, {
                    startY: data.cell.y + 2,
                    tableWidth: data.cell.width - 4,
                    margin: { left: data.cell.x + 2 },
                    head: [['', 'Forward', 'Aft', 'Airdraft', 'SW density']],
                    body: [['Berth to pilot', `${plan.passagePlan.drafts.berthToPilot.foreDraft || plan.foreDraft} m`,
                                              `${plan.passagePlan.drafts.berthToPilot.aftDraft || plan.aftDraft} m`,
                                              `${DataExtractor.getAirDraft(plan, vesselCharacteristics, "berthToPilot")} m`,
                                              `${plan.passagePlan.drafts.berthToPilot.swDensity + " g/cm^3" || ""}`],
                           ['Pilot to pilot',`${plan.passagePlan.drafts.pilotToPilot.foreDraft || plan.foreDraft} m`,
                                              `${plan.passagePlan.drafts.pilotToPilot.aftDraft || plan.aftDraft} m`,
                                              `${DataExtractor.getAirDraft(plan, vesselCharacteristics, "pilotToPilot")} m`,
                                              `${plan.passagePlan.drafts.pilotToPilot.swDensity + " g/cm^3" || ""}`],
                           ['Pilot to berth', `${plan.passagePlan.drafts.pilotToBerth.foreDraft || plan.foreDraft} m`,
                                              `${plan.passagePlan.drafts.pilotToBerth.aftDraft || plan.aftDraft} m`,
                                              `${DataExtractor.getAirDraft(plan, vesselCharacteristics, "pilotToBerth")} m`,
                                              `${plan.passagePlan.drafts.pilotToBerth.swDensity + "g/cm^3" || ""}`],
                           [{ content: 'Note: All under keel clearances calculated on basis of above draughts', colSpan: 5, styles: { fontSize: 8, fontStyle: 'normal' }}],
                        ],
                    theme: 'plain',
                    columnStyles: { 0: { fontStyle: 'bold', fontSize: 8 }},
                    headStyles: { fontStyle: 'bold', fontSize: 8 },
                    rowPageBreak: 'auto',

                    styles: { cellWidth: 'wrap',
                              halign: 'left',
                              lineColor: [0, 0, 0],
                              lineWidth: 0.2,
                              font: "MaerskText",
                              fontSize: 8

                    }
                });
              }
            },
          })
    }

    private _squatsAndUderKeelClearance(calculatedValues: CalculatedValues) {
        const nestedTableCell = {
            content: '',
            // Dynamic height of nested tables are not supported right now
            // so we need to define height of the parent cell
            styles: { minCellHeight: 40, cellWidth: 125 },
        }

        const doc = this._doc;
        const plan = this._plan;

        autoTable(doc, {
            theme: "plain",
            head: [['Calculated squats (see also poster on bridge)', 'Calculated minimum Under Keel Clearance']],
            body: [[nestedTableCell]],
            startY: (doc as any).lastAutoTable.finalY + 7,
            didDrawCell: function (data: { row: { index: number; section: string; }; column: { index: number; }; cell: { y: number; width: number; x: number; }; }) {
              if (data.row.index === 0 && data.row.section === 'body' && data.column.index === 0) {
                autoTable(doc, {
                    startY: data.cell.y + 2,
                    tableWidth: data.cell.width - 4,
                    margin: { left: data.cell.x + 2 },
                    head: [[ '', 'Speed', 'Open waters', 'Confined waters']],
                    body: [[`Min. Speed`,
                            `${calculatedValues.routeMinSpeed ? calculatedValues.routeMinSpeed!.toFixed(1) + " kt" : ""}`,
                            `${plan.passagePlan.squats.minSpeed.openWaters ? plan.passagePlan.squats.minSpeed.openWaters!.toFixed(2) + " m" : ""}`,
                            `${plan.passagePlan.squats.minSpeed.confinedWaters ? plan.passagePlan.squats.minSpeed.confinedWaters!.toFixed(2) + " m" : ""}`],
                            [`Max. Speed`,
                            `${calculatedValues.routeMaxSpeed ? calculatedValues.routeMaxSpeed!.toFixed(1) + " kt" : ""}`,
                            `${plan.passagePlan.squats.maxSpeed.openWaters ? plan.passagePlan.squats.maxSpeed.openWaters!.toFixed(2) + " m" : ""}`,
                            `${plan.passagePlan.squats.maxSpeed.confinedWaters ? plan.passagePlan.squats.maxSpeed.confinedWaters!.toFixed(2) + " m" : ""}`],
                            [`Buffer Speed`,
                            `${calculatedValues.routeAverageSpeed ? calculatedValues.routeAverageSpeed!.toFixed(1) + " kt" : ""}`,
                            `${plan.passagePlan.squats.bufferSpeed.openWaters ? plan.passagePlan.squats.bufferSpeed.openWaters!.toFixed(2) + " m" : ""}`,
                            `${plan.passagePlan.squats.bufferSpeed.confinedWaters ? plan.passagePlan.squats.bufferSpeed.confinedWaters!.toFixed(2) + " m" : ""}`],
                            [`Vessel Max. Speed`,
                            `${calculatedValues.vesselMaxSpeed ? calculatedValues.vesselMaxSpeed.toFixed(1) + " kt" : ""}`,
                            `${plan.passagePlan.squats.vesselMaxSpeed.openWaters ? plan.passagePlan.squats.vesselMaxSpeed.openWaters!.toFixed(2) + " m" : ""}`,
                            `${plan.passagePlan.squats.vesselMaxSpeed.confinedWaters ? plan.passagePlan.squats.vesselMaxSpeed.confinedWaters!.toFixed(2) + " m" : ""}`],
                            ],
                    theme: 'plain',
                    columnStyles: { 0: { fontStyle: 'bold', fontSize: 8 }},
                    headStyles: { fontStyle: 'bold', fontSize: 8 },
                    rowPageBreak: 'auto',

                    styles: { cellWidth: 'wrap',
                            halign: 'left',
                            lineColor: [0, 0, 0],
                            lineWidth: 0.2,
                            font: "MaerskText",
                            fontSize: 8
                    }
                });
              }

              if (data.row.index === 0 && data.row.section === 'body' && data.column.index === 1) {
                autoTable(doc, {
                    startY: data.cell.y + 2,
                    tableWidth: data.cell.width - 4,
                    margin: { left: data.cell.x + 2 },
                    head: [['', 'Required UKC', 'Min. expected UKC']],
                    body: [
                            ['Alongside Departure*', `${DataExtractor.getRequiredUKC("alongsideDeparture", plan)}`, `${plan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected ? plan.passagePlan.underKeelClearance.alongsideDeparture.minimumExpected!.toFixed(1) : ""} m`],
                            ['Alongside Arrival**', `${DataExtractor.getRequiredUKC("alongsideArrival", plan)}`, `${plan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected ? plan.passagePlan.underKeelClearance.alongsideArrival.minimumExpected!.toFixed(1) : ""} m`],
                            ['Confined Water', `${DataExtractor.getRequiredUKC("confinedWater", plan)}`, `${plan.passagePlan.underKeelClearance.confinedWater.minimumExpected ? plan.passagePlan.underKeelClearance.confinedWater.minimumExpected!.toFixed(1) : ""} m`],
                            ['Open Water', `${DataExtractor.getRequiredUKC("openWater", plan)}`, `${plan.passagePlan.underKeelClearance.openWater.minimumExpected ? plan.passagePlan.underKeelClearance.openWater.minimumExpected!.toFixed(1) : ""} m`],
                            [{ content: '* Alongside Departure UKC is calculated based on Planned Draught Berth to pilot\n** Alongside Arrival UKC is calculated based on Planned Draught Pilot to berth', colSpan: 3, styles: { fontSize: 8, fontStyle: 'normal' }}]
                        ],
                    theme: 'plain',
                    columnStyles: { 0: { fontStyle: 'bold', fontSize: 8 }, 1: { fontSize: 8 }, 2: { fontSize: 8 }},
                    headStyles: { fontStyle: 'bold', fontSize: 8 },
                    rowPageBreak: 'auto',

                    styles: { cellWidth: 'wrap',
                            halign: 'left',
                            lineColor: [0, 0, 0],
                            lineWidth: 0.2,
                            font: "MaerskText"
                    }
                });
              }
            },
          })

    }

    private _safetyMessages() {
        // start on new page
        let startY = (this._doc as any).lastAutoTable.finalY + 15;

        if ((this._doc as any).lastAutoTable.finalY > 180) {
            this._doc.addPage();
            startY = 10;
        }
        
        this._doc.setFontSize(11);
        this._doc.setFont("MaerskText", "bold");
        this._doc.text(`Safety messages`, 16, startY);

        const currentSelectedRouteIndex = this._plan!.routes.findIndex(route => route.routeId === this._plan!.currentSelectedRouteId) ?? this._plan?.selectedRouteIndex;

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: startY + 10,
            head: [[{ content: 'EGC (SAT C) and NAVTEX messages to be received during entire voyage', colSpan: 2}],
                   ['NAVAREA region(s)', 'NAVTEX stations' ]],
            body: [[`${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan.routes[currentSelectedRouteIndex], "safetyMessagesNavareaRegions").value}`, `${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan.routes[currentSelectedRouteIndex], "safetyMessagesNavtexStations").value}`]],
            theme: 'plain',
            columnStyles: { 0: { cellWidth: 135 }},
            headStyles: { fontStyle: 'bold', fontSize: 11, halign: 'center' },
            rowPageBreak: 'avoid',

            styles: { cellWidth: 'wrap',
                      halign: 'left',
                      fontSize: 8,
                      lineColor: [0, 0, 0],
                      lineWidth: 0.2,
                      font: "MaerskText"
            }
        });
    }

    private _weatherInformation() {
        // start on new page
        let startY = (this._doc as any).lastAutoTable.finalY + 15;

        if ((this._doc as any).lastAutoTable.finalY > 160) {
            this._doc.addPage();
            startY = 10;
        }

        this._doc.setFontSize(11);
        this._doc.setFont("MaerskText", "bold");
        this._doc.text(`Weather information`, 16, startY);

        const currentSelectedRouteIndex = this._plan!.routes.findIndex(route => route.routeId === this._plan!.currentSelectedRouteId) ?? this._plan.selectedRouteIndex;

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: startY + 10,
            head: [['NAVAREA regions', 'NAVTEX stations', 'Weather information' ]],
            body: [[`${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan.routes[currentSelectedRouteIndex], "weatherInfoNavareaRegions").value}`, 
                    `${DataExtractor.getUserOrSystemInputFromRoutePassagePlan(this._plan.routes[currentSelectedRouteIndex], "weatherInfoNavtexStations").value}`, 
                    `${this._plan.routes[currentSelectedRouteIndex]?.passagePlan!.weatherInfo.info}`]],
            theme: 'plain',
            headStyles: { fontStyle: 'bold', fontSize: 11, halign: 'center' },
            rowPageBreak: 'avoid',
            styles: { cellWidth: 'wrap',
                      halign: 'left',
                      fontSize: 8,
                      lineColor: [0, 0, 0],
                      lineWidth: 0.2,
                      font: "MaerskText"
            }
        });
    }

    private _statusOfNavigation() {
        this._doc.setFontSize(11);
        const currentSelectedRouteIndex = this._plan!.routes.findIndex(route => route.routeId === this._plan!.currentSelectedRouteId) ?? this._plan?.selectedRouteIndex;
        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: (this._doc as any).lastAutoTable.finalY + 10,
            head: [[{ content: 'Status of navigational instruments / other equipment', colSpan: 2}]],
            body: [['Operational condition:', `${this._plan.routes[currentSelectedRouteIndex]?.passagePlan!.equipmentStatus.operationalCondition}`],
                   ['List of failures:', `${this._plan.routes[currentSelectedRouteIndex]?.passagePlan!.equipmentStatus.failures}`],
                  ], 
            theme: 'plain',
            columnStyles: {0: { fontStyle: 'bold', fontSize: 8, cellWidth: 50 }, 1: { halign: 'left', cellWidth: 'auto' }},
            headStyles: { fontStyle: 'bold', fontSize: 11 },
            rowPageBreak: 'auto',
            styles: { font: "MaerskText" },
        });
    }
    
    private _mastersOrders() {
        // start on new page
        let startY = (this._doc as any).lastAutoTable.finalY + 5;
        const currentSelectedRouteIndex = this._plan!.routes.findIndex(route => route.routeId === this._plan!.currentSelectedRouteId) ?? this._plan?.selectedRouteIndex;

        if ((this._doc as any).lastAutoTable.finalY > 170) {
            this._doc.addPage();
            startY = 10;
        }

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: startY,
            head: [['Master\'s orders for this passage']],
            body: [[this._plan.routes[currentSelectedRouteIndex]?.passagePlan!.mastersOrders]],
            theme: 'plain',
            headStyles: { fontStyle: 'bold', fontSize: 11 },
            rowPageBreak: 'auto',

            styles: { cellWidth: 'wrap',
                      fontSize: 8,
                      lineColor: [0, 0, 0],
                      lineWidth: 0.2,
                      font: "MaerskText"
            }
        });
    }

    private _teamList(header: string, list: string[]) {
        // start on new page
        let startY = (this._doc as any).lastAutoTable.finalY + 15;

        if ((this._doc as any).lastAutoTable.finalY > 180) {
            this._doc.addPage();
            startY = 10;
        }

        this._doc.setFontSize(11);
        this._doc.setFont("MaerskText", "bold");
        this._doc.text(`${header}`, 16, startY);

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: startY + 5,
            body: [['Master',
                   'Chief officer',
                   'Second officer',
                   'Third officer',
                   'Pilot',
                   'Helmsman',
                   'Lookout 1',
                   'Lookout 2',
                  ], [...list]],
            theme: 'plain',
            styles: { cellWidth: 'wrap',
                      fontSize: 8,
                      halign: 'center',
                      lineColor: [0, 0, 0],
                      lineWidth: 0.2,
                      font: "MaerskText"
            },
            headStyles: { fontStyle: 'bold', fontSize: 11 },
            rowPageBreak: 'avoid',
        });
    }

    private _various() {
        this._doc.setFontSize(11);
        this._doc.setFont("MaerskText", "bold");
        this._doc.text("Plan read by", 16, (this._doc as any).lastAutoTable.finalY + 10);

        const signatures: string[][] = [];
        this._plan.passagePlan.signatures.forEach(s => signatures.push([s.rank, s.name, "", ""]))

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: (this._doc as any).lastAutoTable.finalY + 15,
            head: [['Rank', 'Name', 'Signature', 'Date']],
            body: signatures,
            theme: 'plain',
            columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 100 }, 3: { cellWidth: 30 }},
            headStyles: { fontStyle: 'bold', fontSize: 8, halign: 'center' },
            styles: { fontSize: 8,
                      halign: 'center',
                      lineColor: [0, 0, 0],
                      lineWidth: 0.2,
                      font: "MaerskText"
            },
        });
    }

    private _approvals() {
        this._doc.setFontSize(11);
        this._doc.setFont("MaerskText", "bold");;
        this._doc.text("Passage Plan Prepared By", 16, (this._doc as any).lastAutoTable.finalY + 10);
        const currentSelectedRouteIndex = this._plan!.routes.findIndex(route => route.routeId === this._plan!.currentSelectedRouteId) ?? this._plan?.selectedRouteIndex;
        const selectedRoute = this._plan.routes[currentSelectedRouteIndex];
        const planDate = selectedRoute?.passagePlan?.planDate || new Date();
        const formattedDate = new Date(planDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).replace(/\//g, '.');

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            },
            startY: (this._doc as any).lastAutoTable.finalY + 15,
            head: [['Date:', 'Captain:', 'Navigation Officer:']],
            body: [[`${formattedDate}`, 
            `${this._plan.routes[currentSelectedRouteIndex]?.passagePlan!.captain ?? ""}`, 
            `${this._plan.routes[currentSelectedRouteIndex]?.passagePlan!.navigationalOfficer ?? ""}`],
            ['Signature:', '', '']
            ],
            theme: 'plain',
            columnStyles: { 0: { cellWidth: 35 }},
            headStyles: { fontStyle: 'bold', fontSize: 11, halign: 'center' },
            rowPageBreak: 'avoid',
            styles: { cellWidth: 'wrap',
                      halign: 'left',
                      fontSize: 8,
                      lineColor: [0, 0, 0],
                      lineWidth: 0.2,
                      font: "MaerskText"
            }
        });
    }

    private _chartGuidelines() {
        this._doc.setFontSize(11);
        this._doc.setFont("MaerskText", "bold");
        this._doc.text(`The following should be marked on the chart, where it enhances safe navigation:`, 16, (this._doc as any).lastAutoTable.finalY + 15);

        autoTable(this._doc, {
            didParseCell: (data: any) => {
                if (data.section === 'head' && data.pageNumber > 1) {
                  data.cell.text = []
                }
            }, 
            startY: (this._doc as any).lastAutoTable.finalY + 20,
            body: [[`- Parallel indexing (not from floating objects unless they have been first checked for position)\n- Methods and frequency of position fixing\n- Last position from previous chart carried forward to next chart\n- Prominent navigation and radar marks\n- No-go areas (the excessive marking of no-go areas should be discouraged - see below)\n- Landfall targets and lights\n- Clearing lines and bearings\n- Transits, heading marks and leading lines\n- Significant tides or current\n- Safe speed and necessary speed alterations\n- Changes in machinery status\n- Minimum under keel clearance\n- Positions where the echo sounder should be activated\n- Crossing and high density traffic areas\n- Safe distance off\n- Anchor clearance\n- Contingency plans, emergency anchoring positions\n- Abort positions\n- VTS and reporting points, etc.`],
                   ['Passage planning information displayed on ECDIS shall be clear, relevant and not excessive, ensuring that critical charted information remains visible and unobscured. No-go areas shall be clearly defined using ECDIS safety settings and user added information, but only where necessary to draw attention to genuine hazards, such as shallow waters, wrecks or obstructions close to the intended track. Excessive or indiscriminate marking of no-go areas should be avoided. Safety settings and route checks shall be reviewed whenever there is a significant change in the vessel’s condition or the passage plan and any updates shall be reflected in the ongoing voyage. Where paper charts are used as a backup arrangement, they shall be corrected and maintained adequately for emergency use. However, ECDIS shall remain the primary reference for navigation and voyage monitoring.'],
                  ],
            theme: 'plain',
            rowPageBreak: 'auto',
            styles: { cellWidth: 'wrap',
                      fontSize: 8,
                      font: "MaerskText" }
        });
    }

    _getDepartureBridgeTeam() {
        return [
            this._plan.passagePlan.bridgeTeam.departure.master ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.departure.chiefOfficer ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.departure.secondOfficer ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.departure.thirdOfficer ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.departure.pilot ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.departure.helmsman ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.departure.lookout1 ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.departure.lookout2 ? 'X' : ' ',
        ];
    }

    _getArrivalBridgeTeam() {
        return [
            this._plan.passagePlan.bridgeTeam.arrival.master ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.arrival.chiefOfficer ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.arrival.secondOfficer ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.arrival.thirdOfficer ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.arrival.pilot ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.arrival.helmsman ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.arrival.lookout1 ? 'X' : ' ',
            this._plan.passagePlan.bridgeTeam.arrival.lookout2 ? 'X' : ' ',
        ];
    }

}
